import React, { useState } from "react";
import "./Login.css";
import logo from "../../asset/feezaap-logo.png";
import { useNavigate, Link } from "react-router-dom";
import { loginUser, sendOtp, registerUser, resetPin } from "../../services/authService";

const PIN_LENGTH = 4;
const createEmptyPin = () => Array(PIN_LENGTH).fill("");

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState(createEmptyPin());
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Registration States
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [instituteName, setInstituteName] = useState("");
  const [instituteCode, setInstituteCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);

  // Forgot PIN States
  const [isForgot, setIsForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [newPinValue, setNewPinValue] = useState("");
  const [confirmPinValue, setConfirmPinValue] = useState("");

  // Popup State
  const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success", onConfirm: null });

  const navigate = useNavigate();

  const resetRegistrationState = () => {
    setOtp(["", "", "", "", "", ""]);
    setName("");
    setEmail("");
    setInstituteName("");
    setInstituteCode("");
    setPassword("");
  };

  const showPopup = (title, message, type = "success", onConfirm = null) => {
    setPopup({ show: true, title, message, type, onConfirm });
  };

  const closePopup = () => {
    const confirmAction = popup.onConfirm;
    setPopup({ ...popup, show: false, onConfirm: null });
    if (confirmAction) confirmAction();
  };

  const handlePinChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    if (value && index < PIN_LENGTH - 1) {
      document.getElementById(`pin-${index + 1}`)?.focus();
    } else if (!value && index > 0) {
      document.getElementById(`pin-${index - 1}`)?.focus();
    } else if (value && index === PIN_LENGTH - 1) {
      const pinValue = newPin.join("");
      if (pinValue.length === PIN_LENGTH) {
        handleLogin(pinValue);
      }
    }
  };

  const handlePinKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      document.getElementById(`pin-${index - 1}`)?.focus();
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    } else if (!value && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleLogin = async (pinOverride) => {
    const pinValue = pinOverride || pin.join("");

    if (mobile.length !== 10) {
      showPopup("Invalid Input", "Please enter a valid 10-digit mobile number", "error");
      return;
    }

    if (pinValue.length !== PIN_LENGTH) {
      showPopup("Invalid PIN", `Please enter your ${PIN_LENGTH}-digit PIN`, "error");
      return;
    }

    try {
      setIsLoggingIn(true);
      const loginRes = await loginUser(mobile, pinValue);
      if (!loginRes.success) {
        showPopup("Login Failed", typeof loginRes.message === "string" && loginRes.message ? loginRes.message : "Invalid mobile number or PIN", "error");
        setPin(createEmptyPin());
        return;
      }
      const data = loginRes?.data;
      // 2️⃣ Store tokens and user info
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("refreshToken", data.refresh_token);
      localStorage.setItem("user", JSON.stringify({
        userName: data.userName,
        userId: data.userId,
        mobileNo: data.mobileNo,
        groupId: data.GroupId,
        roleId: data.RoleId,
        roleName: data.RoleName
      }));

      // 3️⃣ Navigate to Dashboard
      if (data.RoleName === "PARENT") {
        navigate("/dashboard/fees", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }

    } catch (err) {
      console.error("Login failed", err);
      showPopup("Login Failed", typeof err === "string" && err ? err : "Invalid credentials or network error", "error");
      setPin(createEmptyPin());
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSendOtp = async () => {
    if (mobile.length !== 10) {
      showPopup("Invalid Mobile", "Please enter a 10-digit mobile number", "error");
      return;
    }
    try {
      setIsVerifying(true);
      const res = await sendOtp(mobile);

      if (res.success) {
        resetRegistrationState();
        setOtpSent(true);

        showPopup(
          "OTP Sent",
          res?.data?.message || "OTP sent successfully to your mobile.",
          "success"
        );
      } else {
        showPopup("Error", typeof res.message === "string" && res.message ? res.message : "Failed to send OTP. Please try again.", "error");
      }
    } catch (err) {
      console.error("Failed to send OTP", err);
      showPopup("Error", typeof err === "string" && err ? err : "Failed to send OTP. Please try again.", "error");
    } finally {
      setIsVerifying(false);
    }

  };

  const handleResendOtp = async () => {
    try {
      setIsVerifying(true);

      const res = await sendOtp(mobile);

      if (res.success) {
        setOtp(["", "", "", "", "", ""]);

        showPopup(
          "OTP Sent",
          "A new OTP has been sent to your mobile number.",
          "success"
        );
      } else {
        showPopup(
          "Error",
          res.message || "Failed to resend OTP.",
          "error"
        );
      }
    } catch (err) {
      showPopup(
        "Error",
        typeof err === "string" ? err : "Failed to resend OTP.",
        "error"
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRegister = async () => {
    const otpValue = otp.join("");
    if (!name || !email || !instituteName || !instituteCode || password.length !== PIN_LENGTH || otpValue.length < 6) {
      showPopup("Incomplete Form", `Please fill all fields and enter a ${PIN_LENGTH}-digit PIN`, "error");
      return;
    }



    try {
      setIsLoggingIn(true);
      const userData = {
        name,
        mobileNo: mobile,
        email,
        instituteName,
        instituteCode,
        password,
        otpCode: otpValue
      };
      const res = await registerUser(userData);

      if (res.success) {
        showPopup("Success", "Registration successful! Redirecting to dashboard...", "success", async () => {
          // Perform automatic login so the user is authenticated for the dashboard
          try {
            const loginRes = await loginUser(mobile, password);
            if (loginRes.success) {
              const data = loginRes.data;
              localStorage.setItem("token", data.access_token);
              localStorage.setItem("refreshToken", data.refresh_token);
              localStorage.setItem("user", JSON.stringify({
                userName: data.userName,
                userId: data.userId,
                mobileNo: data.mobileNo,
                groupId: data.GroupId,
                roleId: data.RoleId,
                roleName: data.RoleName
              }));

              // Clear registration fields
              setName("");
              setEmail("");
              setInstituteName("");
              setInstituteCode("");
              setPassword("");
              setOtp(["", "", "", "", "", ""]);
              setOtpSent(false);

              navigate("/dashboard", { replace: true });
            } else {
              // If auto-login fails, go to login view so they can try manually
              setIsLogin(true);
              showPopup("Login Required", "Registration was successful, but we couldn't log you in automatically. Please sign in.", "info");
            }
          } catch (err) {
            setIsLogin(true);
          }
        });
      } else {
        showPopup("Registration Failed", typeof res.message === "string" && res.message ? res.message : "Could not complete registration. Please try again.", "error");
      }
    } catch (err) {

      console.error("Registration failed", err);
      showPopup("Registration Failed", typeof err === "string" && err ? err : "Could not complete registration. Please try again.", "error");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleResetPin = async () => {
    const otpValue = otp.join("");
    if (!mobile || otpValue.length < 6 || !newPinValue || !confirmPinValue) {
      showPopup("Missing Info", "Please fill all fields", "error");
      return;
    }

    if (newPinValue !== confirmPinValue) {
      showPopup("Mismatch", "PINs do not match", "error");
      return;
    }

    if (newPinValue.length !== PIN_LENGTH) {
      showPopup("Invalid PIN", `PIN must be ${PIN_LENGTH} digits`, "error");
      return;
    }

    try {
      setIsLoggingIn(true);
      const payload = {
        mobileNo: mobile,
        otpCode: otpValue,
        newPin: newPinValue
      };

      const res = await resetPin(payload);

      if (res.success) {
        showPopup("Success", "PIN has been reset successfully!", "success", () => {
          setIsForgot(false);
          setForgotStep(1);
          setOtp(["", "", "", "", "", ""]);
          setNewPinValue("");
          setConfirmPinValue("");
          setIsLogin(true);
        });
      } else {
        showPopup("Reset Failed", typeof res.message === "string" && res.message ? res.message : "Failed to reset PIN. Please try again.", "error");
      }
    } catch (err) {

      console.error("Reset failed", err);
      showPopup("Reset Failed", typeof err === "string" && err ? err : "Failed to reset PIN. Please try again.", "error");
    } finally {
      setIsLoggingIn(false);
    }
  };



  return (
    <div className="login-page">
      <div className="login-card-split">

        {/* LEFT PANEL */}
        <div className="login-left">
          <div className="login-brand-center">
            <div className="logo-circle">
              <img src={logo} alt="FeeZaap Logo" />
            </div>

            <h2 className="brand-title">FeeZaap</h2>
            <p className="brand-tagline">Tap, Pay, Perform – FeeZaap!</p>
          </div>

        </div>

        {/* RIGHT PANEL */}
        <div className="login-right">
          <div className="login-header-row">
            <h3>{isLogin ? "Sign In" : "Create Account"}</h3>
            <Link to="/" className="back-to-website">
              <i className="fas fa-arrow-left"></i>
              Back to Website
            </Link>
          </div>

          <div className="form-container">
            {isForgot ? (
              <>
                <h3>Reset Your PIN</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
                  {forgotStep === 1
                    ? "Enter your mobile number to receive a reset OTP."
                    : `Enter the OTP sent to your phone and set your new ${PIN_LENGTH}-digit PIN.`}
                </p>

                {forgotStep === 1 ? (
                  <>
                    <label>Mobile Number</label>
                    <div className="input-group">
                      <input
                        type="tel"
                        placeholder="Enter mobile number"
                        value={mobile}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setMobile(val);
                        }}
                        maxLength={10}
                        style={{
                          height: "65px",
                        }}
                      />
                      <button
                        className="verify-btn"
                        onClick={async () => {
                          if (mobile.length !== 10) {
                            showPopup("Invalid", "Enter 10-digit mobile number", "error");
                            return;
                          }
                          try {
                            setIsVerifying(true);
                            await sendOtp(mobile);
                            setForgotStep(2);
                            showPopup("OTP Sent", "Reset OTP has been sent to your mobile.", "success");
                          } catch (err) {
                            showPopup("Error", typeof err === "string" && err ? err : "Failed to send OTP", "error");
                          } finally {
                            setIsVerifying(false);
                          }
                        }}
                        disabled={isVerifying || mobile.length !== 10}
                      >
                        {isVerifying ? <div className="spinner mini"></div> : "Send OTP"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mobile-info-badge">
                      <span>Resetting for: <strong>+91 {mobile}</strong></span>
                      <button onClick={() => setForgotStep(1)}>Change</button>
                    </div>

                    <label>Verify OTP</label>
                    <div className="otp-digits-container" style={{ marginBottom: '20px' }}>
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        />
                      ))}
                    </div>



                    <label>New {PIN_LENGTH}-Digit PIN</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Enter new PIN"
                      value={newPinValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) && val.length <= PIN_LENGTH) {
                          setNewPinValue(val);
                        }
                      }}

                      maxLength={PIN_LENGTH}
                    />

                    <label>Confirm New PIN</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Confirm new PIN"
                      value={confirmPinValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) && val.length <= PIN_LENGTH) {
                          setConfirmPinValue(val);
                        }
                      }}

                      maxLength={PIN_LENGTH}
                    />

                    <button
                      className="login-btn"
                      onClick={handleResetPin}
                      disabled={isLoggingIn}
                    >
                      {isLoggingIn ? "Resetting..." : "Reset PIN"}
                    </button>
                  </>
                )}

                <button
                  className="switch-mode-btn"
                  style={{ marginTop: '20px', display: 'block', textAlign: 'center', width: '100%' }}
                  onClick={() => {
                    setIsForgot(false);
                    setForgotStep(1);
                  }}
                >
                  Back to <span>Login</span>
                </button>
              </>
            ) : isLogin ? (
              <>
                <label>Mobile Number</label>
                <input
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={mobile}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setMobile(val);
                      if (val.length === 10) {
                        document.getElementById("pin-0")?.focus();
                      }
                    }
                  }}
                  maxLength={10}
                  style={{
                    height: "65px",
                  }}

                />

                <label>Enter PIN</label>
                <div className="pin-container">
                  {pin.map((digit, index) => (
                    <input
                      key={index}
                      id={`pin-${index}`}
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) =>
                        handlePinChange(e.target.value, index)
                      }
                      onKeyDown={(e) => handlePinKeyDown(e, index)}
                      style={{
                        height: "65px",
                        width: "52px",
                      }}

                    />
                  ))}
                </div>

                <button
                  className="login-btn"
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? "Signing in..." : "Login"}
                </button>

                <div className="form-footer">
                  <button
                    className="forgot-btn"
                    onClick={() => {
                      setIsForgot(true);
                      setForgotStep(1);
                      setOtp(["", "", "", "", "", ""]);
                    }}
                  >
                    Forgot PIN?
                  </button>

                  <button
                    className="switch-mode-btn"
                    onClick={() => {
                      resetRegistrationState();
                      setIsLogin(false);
                    }}
                  >
                    New User? <span>Create Account</span>
                  </button>
                </div>
              </>
            ) : (

              <>
                {!otpSent ? (
                  <>
                    <label>Enter WhatsApp Number</label>
                    <div className="input-group">
                      <input
                        type="tel"
                        placeholder="Enter WhatsApp number"
                        value={mobile}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setMobile(val);
                        }}
                        maxLength={10}
                      />
                      <button
                        className="verify-btn"
                        onClick={handleSendOtp}
                        disabled={isVerifying || mobile.length !== 10}
                      >
                        {isVerifying ? <div className="spinner mini"></div> : "Send OTP"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mobile-info-badge">
                      <span>Registering for: <strong>+91 {mobile}</strong></span>
                      <button
                        onClick={() => {
                          resetRegistrationState();
                          setOtpSent(false);
                        }}
                      >
                        Change
                      </button>
                    </div>

                    <label>Verify OTP</label>
                    <div className="otp-digits-container" style={{ marginBottom: '20px' }}>
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        />
                      ))}
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <button
                        type="button"
                        className="switch-mode-btn"
                        onClick={handleResendOtp}
                        disabled={isVerifying}
                      >
                        {isVerifying ? "Sending..." : "Resend OTP"}
                      </button>
                    </div>

                    <label>User Name</label>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />

                    <label>Institute Name</label>
                    <input
                      type="text"
                      placeholder="Enter institute name"
                      value={instituteName}
                      onChange={(e) => {
                        const val = e.target.value;
                        setInstituteName(val);

                        // Generate Institute Code automatically
                        const words = val.trim().split(/\s+/).filter(w => w.length > 0);
                        let code = "";
                        if (words.length >= 3) {
                          code = words[0][0] + words[1][0] + words[2][0];
                        } else if (words.length === 2) {
                          code = words[0][0] + words[1].substring(0, 2);
                        } else if (words.length === 1) {
                          code = words[0].substring(0, 3);
                        }
                        setInstituteCode(code.toUpperCase());
                      }}
                    />

                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />

                    <label>Institute Code</label>
                    <input
                      type="text"
                      placeholder="Enter institute code"
                      value={instituteCode}
                      onChange={(e) => setInstituteCode(e.target.value)}
                    />

                    <label>Set {PIN_LENGTH}-Digit PIN</label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder={`Enter ${PIN_LENGTH}-digit PIN`}
                      value={password}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) && val.length <= PIN_LENGTH) {
                          setPassword(val);
                        }
                      }}
                      maxLength={PIN_LENGTH}
                    />


                    <button
                      className="login-btn"
                      onClick={handleRegister}
                      disabled={isLoggingIn}
                    >
                      {isLoggingIn ? "Registering..." : "Complete Registration"}
                    </button>
                  </>
                )}

                <button
                  className="switch-mode-btn"
                  onClick={() => {
                    setIsLogin(true);
                    setOtpSent(false);
                    resetRegistrationState();
                  }}
                >
                  Already have an account? <span>Login</span>
                </button>
              </>
            )}


          </div>
        </div>

      </div>

      <Popup popup={popup} onClose={closePopup} />
    </div>
  );
};

const Popup = ({ popup, onClose }) => {
  if (!popup.show) return null;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-card" onClick={(e) => e.stopPropagation()}>
        <div className={`popup-icon ${popup.type}`}>
          {popup.type === "success" ? "✓" : "!"}
        </div>
        <h3 className="popup-title">{popup.title}</h3>
        <p className="popup-message">{popup.message}</p>
        <button className="popup-confirm-btn" onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
};

export default Login;
